/// Live ADS-B feed for the Handoff levels. Polls a same-origin proxy
/// (`/api/live-traffic-nyc` → Cloud Function → adsb.lol), classifies each
/// aircraft, and queues new spawns. Mirrors iOS `LiveTrafficFeed`:
/// one-way valve (each hex spawned once), sticky airport assignment, freshest-
/// position dedup, and a poll-to-poll altitude-trend fallback.

import {
  type AdsbAircraft,
  type FlightPhase,
  type RegionAirport,
  N90_AIRPORTS,
  assignAirport,
  classifyPhase,
  displayCallsign,
  inferActiveFlowTrueHeading,
} from './classifier'

export interface LiveSpawn {
  hex: string
  callsign: string
  icaoType?: string
  category?: string
  lat: number
  lon: number
  altitudeFt: number
  groundSpeedKt: number
  trackDeg: number
  verticalRateFpm: number | null
  navAltitudeFt: number | null
  phase: FlightPhase
  assignedAirportICAO: string | null
}

export interface FeedStatus {
  degraded: boolean
  total: number
}

export class TrafficFeed {
  private readonly spawnedHexes = new Set<string>()
  private readonly stickyAssignments = new Map<string, string>()
  private readonly lastAltitude = new Map<string, number>()
  private queue: LiveSpawn[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private stopped = false
  private consecutiveFailures = 0
  degraded = false

  constructor(
    private readonly endpoint: string,
    private readonly airports: readonly RegionAirport[] = N90_AIRPORTS,
    private readonly pollSeconds = 7.5,
    private readonly onStatus?: (s: FeedStatus) => void,
    /// The active landing flow's TRUE heading, inferred from descending live
    /// traffic each poll — drives the game's active runway to match reality.
    private readonly onActiveFlow?: (trueHeadingDeg: number) => void,
  ) {}

  /// Kick off the seed fetch + the jittered poll loop.
  start(): void {
    void this.poll().finally(() => this.schedule())
  }

  stop(): void {
    this.stopped = true
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  /// Pop up to `max` queued spawns (drained a few per tick to avoid bursts).
  drain(max: number): LiveSpawn[] {
    return this.queue.splice(0, max)
  }

  pendingCount(): number {
    return this.queue.length
  }

  private schedule(): void {
    if (this.stopped) return
    const jitterMs = Math.random() * 1500
    this.timer = setTimeout(() => {
      void this.poll().finally(() => this.schedule())
    }, this.pollSeconds * 1000 + jitterMs)
  }

  private async poll(): Promise<void> {
    try {
      const res = await fetch(this.endpoint, { signal: AbortSignal.timeout(6000) })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const pic = (await res.json()) as { ac?: AdsbAircraft[]; total?: number }
      this.consecutiveFailures = 0
      if (this.degraded) this.degraded = false
      this.process(pic.ac ?? [])
      this.onStatus?.({ degraded: false, total: pic.total ?? pic.ac?.length ?? 0 })
      const flow = inferActiveFlowTrueHeading(pic.ac ?? [], this.airports)
      if (flow != null) this.onActiveFlow?.(flow)
    } catch {
      this.consecutiveFailures++
      // ~45s of failures before annunciating (6 polls × ~7.5s).
      if (this.consecutiveFailures >= 6 && !this.degraded) {
        this.degraded = true
        this.onStatus?.({ degraded: true, total: 0 })
      }
    }
  }

  private process(ac: AdsbAircraft[]): void {
    // Dedup by hex; freshest position (lowest seen_pos) wins.
    const freshest = new Map<string, AdsbAircraft>()
    for (const a of ac) {
      const ex = freshest.get(a.hex)
      if (ex && (ex.seen_pos ?? 0) <= (a.seen_pos ?? 0)) continue
      freshest.set(a.hex, a)
    }

    for (const a of freshest.values()) {
      if (this.spawnedHexes.has(a.hex)) continue // one-way valve
      if (a.lat == null || a.lon == null || typeof a.alt_baro !== 'number') continue

      let phase = classifyPhase(a)
      if (phase === 'level_low' || phase === 'unknown') {
        const prev = this.lastAltitude.get(a.hex)
        if (prev != null) {
          if (a.alt_baro > prev + 100) phase = 'departure'
          else if (a.alt_baro < prev - 100) phase = 'arrival'
        }
      }
      this.lastAltitude.set(a.hex, a.alt_baro)
      if (phase !== 'arrival' && phase !== 'departure') continue

      // Sticky assignment: once confident on a hex, keep it.
      let airport = this.stickyAssignments.get(a.hex) ?? null
      if (airport == null) {
        airport = assignAirport(a, phase, this.airports)
        if (airport) this.stickyAssignments.set(a.hex, airport)
      }
      if (airport == null) continue // hold for next poll — honest unknowns > wrong answers
      if (a.track == null) continue

      this.spawnedHexes.add(a.hex)
      this.queue.push({
        hex: a.hex,
        callsign: displayCallsign(a),
        icaoType: a.t,
        category: a.category,
        lat: a.lat,
        lon: a.lon,
        altitudeFt: a.alt_baro,
        groundSpeedKt: a.gs ?? 250,
        trackDeg: a.track,
        verticalRateFpm: a.baro_rate ?? a.geom_rate ?? null,
        navAltitudeFt: a.nav_altitude_fms ?? a.nav_altitude_mcp ?? null,
        phase,
        assignedAirportICAO: airport,
      })
    }
  }
}
